import { useEffect, useRef, useState } from "react";
import { Heart, Phone, MapPin, Volume2, VolumeX, Share2, MessageCircle, Play, Store, Eye } from "lucide-react";
import { fileUrl, api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function VideoCard({ product, active, muted, onToggleMute }) {
    const videoRef = useRef(null);
    const [liked, setLiked] = useState(false);
    const [likes, setLikes] = useState(product.likes || 0);
    const [views, setViews] = useState(product.views || 0);
    const [descExpanded, setDescExpanded] = useState(false);
    const [showHeart, setShowHeart] = useState(false);
    const [ordering, setOrdering] = useState(false);
    const [playing, setPlaying] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (active) {
            v.currentTime = 0;
            v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
            api.post(`/products/${product.id}/view`)
                .then(() => setViews((n) => n + 1))
                .catch(() => {});
        } else {
            v.pause();
            setPlaying(false);
        }
    }, [active, product.id]);

    useEffect(() => {
        if (!user) return;
        api.get(`/products/${product.id}/liked`)
            .then((r) => setLiked(r.data.liked))
            .catch(() => {});
    }, [product.id, user]);

    const doLike = async () => {
        if (!user) return navigate("/login");
        const wasLiked = liked;
        setLiked(!wasLiked);
        setLikes((n) => n + (wasLiked ? -1 : 1));
        try {
            const r = await api.post(`/products/${product.id}/like`);
            setLiked(r.data.liked);
        } catch {
            setLiked(wasLiked);
            setLikes((n) => n + (wasLiked ? 1 : -1));
        }
    };

    const onDoubleTap = () => {
        setShowHeart(true);
        setTimeout(() => setShowHeart(false), 900);
        if (!liked) doLike();
    };

    const handleTap = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) { v.play(); setPlaying(true); }
        else { v.pause(); setPlaying(false); }
    };

    const orderWhatsApp = async () => {
        if (ordering) return;
        setOrdering(true);
        try {
            const { data } = await api.post(`/products/${product.id}/order`);
            window.open(data.whatsapp_url, "_blank");
            toast.success("Redirection vers WhatsApp...");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Erreur lors de la commande";
            toast.error(msg);
        } finally {
            setOrdering(false);
        }
    };

    const call = () => {
        if (product.call_number) {
            window.location.href = `tel:${product.call_number}`;
        } else {
            toast.error("Numéro d'appel non disponible");
        }
    };

    const share = async () => {
        const url = `${window.location.origin}/feed?p=${product.id}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: product.title, text: `${product.title} - ${product.price} FCFA sur VIDMAR`, url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Lien copié !");
            }
        } catch (err) {
            // User dismissed share or clipboard blocked
            console.debug("Share cancelled", err?.name);
        }
    };

    let lastTap = 0;
    const onTouch = () => {
        const now = Date.now();
        if (now - lastTap < 300) { onDoubleTap(); }
        else { handleTap(); }
        lastTap = now;
    };

    const priceStr = `${Number(product.price).toLocaleString("fr-FR")} ${product.currency || "FCFA"}`;

    return (
        <div className="feed-item relative w-full bg-black overflow-hidden" data-testid={`video-card-${product.id}`}>
            {/* Video */}
            <div className="absolute inset-0 flex items-center justify-center bg-black" onClick={onTouch}>
                <video
                    ref={videoRef}
                    src={fileUrl(product.video_path)}
                    poster={fileUrl(product.image_path)}
                    className="w-full h-full object-contain bg-black"
                    playsInline
                    loop
                    muted={muted}
                    preload="metadata"
                    data-testid={`video-player-${product.id}`}
                />
                {!playing && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="w-20 h-20 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center">
                            <Play className="w-10 h-10 text-white fill-white" />
                        </span>
                    </div>
                )}
            </div>

            {/* Heart pop */}
            {showHeart && (
                <Heart className="absolute top-1/2 left-1/2 w-32 h-32 text-[#FF5722] fill-[#FF5722] heart-pop pointer-events-none z-30" />
            )}

            {/* Right action bar */}
            <div className="absolute right-3 bottom-40 flex flex-col gap-5 items-center z-40">
                <button
                    data-testid="mute-toggle-btn"
                    onClick={onToggleMute}
                    className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                >
                    <span className="w-12 h-12 rounded-full glass flex items-center justify-center">
                        {muted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
                    </span>
                    <span className="text-[10px] text-white text-shadow-strong font-body">{muted ? "Son" : "Muet"}</span>
                </button>
                <button
                    data-testid={`like-btn-${product.id}`}
                    onClick={doLike}
                    className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                >
                    <span className="w-12 h-12 rounded-full glass flex items-center justify-center">
                        <Heart className={`w-6 h-6 ${liked ? "text-[#FF5722] fill-[#FF5722]" : "text-white"}`} strokeWidth={2} />
                    </span>
                    <span className="text-xs text-white text-shadow-strong font-body font-semibold">{likes}</span>
                </button>
                <button
                    data-testid={`share-btn-${product.id}`}
                    onClick={share}
                    className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                >
                    <span className="w-12 h-12 rounded-full glass flex items-center justify-center">
                        <Share2 className="w-5 h-5 text-white" />
                    </span>
                    <span className="text-[10px] text-white text-shadow-strong font-body">Partager</span>
                </button>
                <div
                    data-testid={`views-count-${product.id}`}
                    className="flex flex-col items-center gap-1"
                >
                    <span className="w-12 h-12 rounded-full glass flex items-center justify-center">
                        <Eye className="w-5 h-5 text-white" />
                    </span>
                    <span className="text-xs text-white text-shadow-strong font-body font-semibold">{views}</span>
                </div>
                <button
                    data-testid={`call-btn-${product.id}`}
                    onClick={call}
                    className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                >
                    <span className="w-12 h-12 rounded-full glass flex items-center justify-center">
                        <Phone className="w-5 h-5 text-white" />
                    </span>
                    <span className="text-[10px] text-white text-shadow-strong font-body">Appeler</span>
                </button>
            </div>

            {/* Bottom product sheet */}
            <div className="absolute bottom-0 left-0 right-0 pb-24 pt-16 px-4 bg-gradient-to-t from-black/95 via-black/70 to-transparent z-30">
                <div className="max-w-md">
                    <div className="flex items-center gap-2 mb-2">
                        <button
                            data-testid={`open-shop-${product.id}`}
                            onClick={() => product.shop_slug && navigate(`/shop/${product.shop_slug}`)}
                            className="flex items-center gap-2 active:scale-95 transition-transform"
                        >
                            <div className="w-9 h-9 rounded-full bg-[#FF5722]/20 border border-white/10 overflow-hidden flex items-center justify-center">
                                {product.seller_picture ? (
                                    <img src={product.seller_picture} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="font-display font-bold text-sm text-white">{(product.seller_name || "M").charAt(0).toUpperCase()}</span>
                                )}
                            </div>
                            <div className="text-left">
                                <p className="font-body font-semibold text-sm text-white text-shadow-strong flex items-center gap-1" data-testid={`seller-name-${product.id}`}>
                                    {product.seller_name}
                                    {product.shop_slug && <Store className="w-3 h-3 text-white/70" />}
                                </p>
                                <div className="flex items-center gap-1 text-white/80 text-shadow-strong">
                                    <MapPin className="w-3 h-3" />
                                    <span className="text-[11px] font-body">{product.city}</span>
                                </div>
                            </div>
                        </button>
                    </div>

                    <h2 className="font-display font-bold text-xl text-white text-shadow-strong mb-1" data-testid={`product-title-${product.id}`}>
                        {product.title}
                    </h2>
                    <div className="mb-3">
                        <p
                            className={`text-white/90 text-sm font-body text-shadow-strong whitespace-pre-line ${descExpanded ? "" : "line-clamp-2"}`}
                            data-testid={`product-desc-${product.id}`}
                        >
                            {product.description}
                        </p>
                        {(product.description || "").length > 90 && (
                            <button
                                onClick={() => setDescExpanded((v) => !v)}
                                data-testid={`toggle-desc-${product.id}`}
                                className="text-[#FFC107] font-body font-semibold text-xs mt-1 active:scale-95"
                            >
                                {descExpanded ? "Voir moins" : "Voir plus"}
                            </button>
                        )}
                    </div>

                    <div className="flex items-end justify-between gap-3">
                        <div>
                            <p className="text-[11px] text-white/60 font-body uppercase tracking-wide">Prix</p>
                            <p className="font-display text-3xl font-black text-white text-shadow-strong" data-testid={`product-price-${product.id}`}>
                                {priceStr}
                            </p>
                        </div>
                        <button
                            data-testid={`whatsapp-order-btn-${product.id}`}
                            onClick={orderWhatsApp}
                            disabled={ordering}
                            className="wa-btn h-14 px-6 rounded-full text-white font-body font-bold text-base flex items-center gap-2 disabled:opacity-60"
                        >
                            <MessageCircle className="w-5 h-5 fill-white text-[#25D366]" />
                            {ordering ? "Envoi..." : "Commander"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
